import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, MapPin, ExternalLink, Phone, Tent, Star, ArrowRight, Loader2 } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  usePageTitle('Payment Confirmed - Elevated Escape');
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [orderInfo, setOrderInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) { setStatus('failed'); return; }

    let attempts = 0;
    let active = true;

    function poll() {
      fetch(`${API}/checkout/status/${sessionId}`)
        .then(function(res) { return res.ok ? res.json() : null; })
        .then(function(data) {
          if (!active) return;
          if (data && data.payment_status === 'paid') {
            setStatus('success');
            setOrderInfo(data);
          } else if (data && (data.payment_status === 'unpaid' || data.status === 'expired')) {
            setStatus('failed');
          } else {
            attempts += 1;
            if (attempts < 15) { setTimeout(poll, 2500); } else { setStatus('failed'); }
          }
        })
        .catch(function() {
          if (!active) return;
          attempts += 1;
          if (attempts < 15) { setTimeout(poll, 2500); } else { setStatus('failed'); }
        });
    }
    poll();

    return function() { active = false; };
  }, [searchParams]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-[#030012] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-brand-gold mx-auto mb-4 animate-spin" />
          <p className="text-white text-lg mb-2">Confirming your payment...</p>
          <p className="text-gray-500 text-sm">This may take a moment.</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-[#030012] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <p className="text-red-400 text-lg mb-4">We couldn&apos;t confirm your payment.</p>
          <p className="text-gray-400 text-sm mb-6">If you were charged, don&apos;t worry. Please contact us and we&apos;ll sort it out.</p>
          <div className="flex flex-col gap-3">
            <a href="mailto:admin@elevatingmn.com" className="text-brand-gold hover:underline">admin@elevatingmn.com</a>
            <Link to="/elevated-escape/tickets" className="text-gray-400 hover:text-white text-sm">Back to tickets</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030012]" data-testid="payment-success-page">
      {/* Success Header */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 border border-green-500/30 mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3" data-testid="success-heading">You&apos;re In!</h1>
          <p className="text-xl text-gray-300 mb-2">Your ticket to The Elevated Escape has been confirmed.</p>
          <p className="text-brand-gold text-sm font-medium mb-6">August 21-23, 2026 &middot; Camp Waub-O-Jeeg, Taylors Falls, MN</p>
          <div className="bg-red-600/20 border-2 border-red-500/50 p-4 max-w-xl mx-auto">
            <p className="text-red-400 font-bold text-sm uppercase tracking-wide mb-1">Your reservation is NOT complete yet</p>
            <p className="text-gray-300 text-sm">You must call the campground to finalize your spot.</p>
          </div>
        </div>
      </section>

      {/* Important Next Step */}
      <section className="pb-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="relative p-[3px] neon-border-wrap">
            <div className="absolute inset-0 neon-border-glow" />
            <div className="relative bg-[#0a0520] p-8 lg:p-10" data-testid="reservation-cta">
              <div className="flex items-start gap-4 mb-6">
                <Phone className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">REQUIRED: Call to Finalize Your Reservation</h2>
                  <p className="text-gray-300 leading-relaxed">
                    Your event ticket is confirmed, but <span className="text-red-400 font-bold">your reservation is NOT complete</span> until you call the campground and reserve your specific camping spot for August 21-23.
                  </p>
                </div>
              </div>

              {/* Phone number - prominent */}
              <div className="bg-white/10 border-2 border-brand-gold/50 p-6 mb-6 text-center" data-testid="call-cta">
                <p className="text-brand-gold text-xs uppercase tracking-widest mb-2">Call Now to Reserve Your Spot</p>
                <a href="tel:6514653500" className="text-4xl lg:text-5xl font-bold text-white hover:text-brand-gold transition-colors" data-testid="phone-number-link">
                  (651) 465-3500
                </a>
                <p className="text-white font-semibold mt-3">Ask for Deb &middot; Mention &ldquo;Elevated Escape&rdquo;</p>
              </div>

              <div className="bg-white/5 border border-white/10 p-6 mb-6">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-brand-gold" /> Steps to Finalize
                </h3>
                <ol className="text-gray-300 text-sm space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    <span><span className="text-white font-bold">Call (651) 465-3500</span> and ask for <span className="text-white font-bold">Deb</span></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-brand-gold text-brand-dark w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    <span>Tell them you&apos;re attending <span className="text-white font-medium">The Elevated Escape</span> on <span className="text-white font-medium">August 21-23, 2026</span></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-brand-gold text-brand-dark w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                    <span>Pick your preferred camping spot using the map below. The best sites go first!</span>
                  </li>
                </ol>
              </div>

              <a
                href="tel:6514653500"
                className="neon-btn w-full justify-center text-lg"
                data-testid="call-now-btn"
              >
                <span className="neon-btn-glow" />
                <Phone className="w-5 h-5" />
                Call (651) 465-3500 Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Camp Map */}
      <section className="pb-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-white mb-2 flex items-center justify-center gap-2">
              <MapPin className="w-5 h-5 text-brand-gold" /> Camp Waub-O-Jeeg Site Map
            </h3>
            <p className="text-gray-400 text-sm">Choose your spot and reserve it with the campground</p>
          </div>
          <div className="bg-white p-2 lg:p-4" data-testid="camp-map">
            <img
              src="/images/site/campmap.png"
              alt="Camp Waub-O-Jeeg campsite map showing A-Hill Primitive Sites, A-Hill Electric Sites, and B-Hill Electric Sites"
              className="w-full h-auto"
            />
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
            <div className="bg-white/5 border border-white/10 p-4">
              <Star className="w-5 h-5 text-brand-gold mx-auto mb-2" />
              <p className="text-white text-sm font-medium">A-Hill Primitive</p>
              <p className="text-gray-500 text-xs">Rustic tent sites, no electric</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4">
              <Star className="w-5 h-5 text-brand-gold mx-auto mb-2" />
              <p className="text-white text-sm font-medium">A-Hill Electric</p>
              <p className="text-gray-500 text-xs">Sites with electric hookup</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4">
              <Star className="w-5 h-5 text-brand-gold mx-auto mb-2" />
              <p className="text-white text-sm font-medium">B-Hill Electric</p>
              <p className="text-gray-500 text-xs">Sites with electric hookup</p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Next */}
      <section className="py-12 bg-[#06021a] border-t border-white/5">
        <div className="mx-auto max-w-3xl px-6 lg:px-8 text-center">
          <p className="text-brand-gold/70 text-sm font-medium mb-6">You are not alone.</p>
          <p className="text-gray-400 mb-4">Questions about the event?</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="mailto:admin@elevatingmn.com" className="text-brand-gold font-medium hover:underline">admin@elevatingmn.com</a>
            <span className="text-gray-600">|</span>
            <Link to="/elevated-escape/tickets" className="text-brand-gold font-medium hover:underline flex items-center gap-1">
              Back to Event Page <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PaymentSuccessPage;
